import type { Client } from '@xmtp/browser-sdk';

export interface ConversationData {
  id: string;
  topic: string;
  peerAddress: string;       // For DMs - the other participant's address
  isGroup: boolean;
  groupName?: string;
  members?: string[];         // Group member addresses
  createdAt: Date;
  lastMessageText?: string;
  lastMessageTime?: Date;
  lastMessageSender?: string;
}

export interface ChatMessage {
  id: string;
  conversationId: string;
  senderAddress: string;
  content: any;
  contentType: string;
  sentAt: Date;
  isSentByMe: boolean;
}

export interface ChatState {
  // Client state
  client: Client | null;
  isInitializing: boolean;
  isInitialized: boolean;
  initError: string | null;
  initProgress: string | null;

  // Conversation state
  conversations: ConversationData[];
  activeConversationId: string | null;

  // Message state
  messages: Record<string, ChatMessage[]>;  // conversationId -> messages
  loadingMessages: Record<string, boolean>;
  hasMoreMessages: Record<string, boolean>;

  // Unread tracking
  unreadCounts: Record<string, number>;
  totalUnread: number;

  // UI state
  searchQuery: string;
  isSyncing: boolean;
  conversationFilter: 'all' | 'dms' | 'groups';

  // Installation limit state
  showInstallationManager: boolean;
  pendingClient: Client | null;
  pendingInboxId: string | null;
}

export type ChatAction =
  | { type: 'SET_CLIENT'; payload: Client }
  | { type: 'SET_INITIALIZING'; payload: boolean }
  | { type: 'SET_INITIALIZED'; payload: boolean }
  | { type: 'SET_INIT_ERROR'; payload: string | null }
  | { type: 'SET_INIT_PROGRESS'; payload: string | null }
  | { type: 'SET_CONVERSATIONS'; payload: ConversationData[] }
  | { type: 'ADD_CONVERSATION'; payload: ConversationData }
  | { type: 'UPDATE_CONVERSATION_LAST_MESSAGE'; payload: { id: string; text: string; time: Date; sender: string } }
  | { type: 'UPDATE_CONVERSATION_MEMBERS'; payload: { id: string; members: string[] } }
  | { type: 'SET_ACTIVE_CONVERSATION'; payload: string | null }
  | { type: 'SET_MESSAGES'; payload: { conversationId: string; messages: ChatMessage[] } }
  | { type: 'APPEND_MESSAGE'; payload: { conversationId: string; message: ChatMessage } }
  | { type: 'PREPEND_MESSAGES'; payload: { conversationId: string; messages: ChatMessage[] } }
  | { type: 'SET_LOADING_MESSAGES'; payload: { conversationId: string; loading: boolean } }
  | { type: 'SET_HAS_MORE_MESSAGES'; payload: { conversationId: string; hasMore: boolean } }
  | { type: 'INCREMENT_UNREAD'; payload: string }
  | { type: 'CLEAR_UNREAD'; payload: string }
  | { type: 'SET_SEARCH_QUERY'; payload: string }
  | { type: 'SET_SYNCING'; payload: boolean }
  | { type: 'SET_CONVERSATION_FILTER'; payload: 'all' | 'dms' | 'groups' }
  | { type: 'SET_INSTALLATION_LIMIT'; payload: { client: Client | null; inboxId: string } }
  | { type: 'CLEAR_INSTALLATION_LIMIT' }
  | { type: 'RESET' };

export const initialChatState: ChatState = {
  client: null,
  isInitializing: false,
  isInitialized: false,
  initError: null,
  initProgress: null,
  conversations: [],
  activeConversationId: null,
  messages: {},
  loadingMessages: {},
  hasMoreMessages: {},
  unreadCounts: {},
  totalUnread: 0,
  searchQuery: '',
  isSyncing: false,
  conversationFilter: 'all',
  showInstallationManager: false,
  pendingClient: null,
  pendingInboxId: null,
};
